import React from 'react';
import _ from 'lodash';
import { Button, Box, Stack, Typography, Paper, Table, TableContainer, TableCell, TableHead, TableRow, TableBody } from '@mui/material';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useMutation } from 'react-fetching-library';
import { DebounceInput } from 'react-debounce-input';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

import { SmallFormTextField } from '../../../../../components/small-fields/SmallFormTextField';
import { AlertDialogContainer } from '../../../../../components/others/AlertDialogContainer';
import { submitPaymentDateRangeAction } from '../../../../../services/request';
import { ConfirmDialog } from '../../../../../components/ConfirmDialog';
import { SmallFormDateField } from '../../../../../components/small-fields/SmallFormDateField';
import { formatDate } from '../../../../../services/functions';
import { Row } from './Row';

export const PaymentDateRangeForm = props => {
  const { data, submitCallback, handleClose, allChoices } = props;
  const dynamicRef = React.useRef([]);
  const { loading, mutate } = useMutation(submitPaymentDateRangeAction);
  const [showAlert, setShowAlert] = React.useState(false);
  const [severity, setSeverity] = React.useState('');
  const [title, setTitle] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [confirmDeleteOpen, setConfirmDeleteOpen] = React.useState(false);
  const [positions, setPositions] = React.useState([]);

  React.useEffect(() => {
    if (!_.isNil(data) && data.gross_values_per_participants_range) {
      setPositions(data.gross_values_per_participants_range);
    }
  }, [data]);

  const validationSchema = Yup.object({
    date_from: Yup.date().required('To pole jest wymagane'),
    date_to: Yup.date().required('To pole jest wymagane'),
    gross_value_per_paid_guardian: Yup.number().required('To pole jest wymagane')
  });

  const initialValues = {
    periods: data ? data.periods : [],
    packages: data ? data.packages : [],
    date_from: data ? data.date_from : null,
    date_to: data ? data.date_to : null,
    gross_value_per_paid_guardian: data ? data.gross_value_per_paid_guardian : 0,
    minimum_goal_giewartow: data ? data.minimum_goal_giewartow : 0,
    maximum_goal_giewartow: data ? data.maximum_goal_giewartow : 0,
    minimum_goal_mrowki: data ? data.minimum_goal_mrowki : 0,
    maximum_goal_mrowki: data ? data.maximum_goal_mrowki : 0
  };

  const handleSubmit = async values => {
    if (getAllErrors().length === 0) {
      setSeverity('error');
      setTitle('Formularz zawiera błędy');
      setMessage('');
      setShowAlert(true);

      return;
    }
    let hasErrors = false;
    _.map(getAllErrors(), obj => {
      if (obj.errors.length > 0 || obj.requiredFields.length > 0) {
        hasErrors = true;
      }
    });
    if (hasErrors) {
      setSeverity('error');
      setTitle('Formularz zawiera błędy');
      setMessage('');
      setShowAlert(true);

      return;
    }
    values.gross_values_per_participants_range = positions;
    const { error, payload } = await mutate(values, data ? 'PUT' : 'POST', data ? data.id : undefined);
    if (!error) {
      submitCallback(payload, data ? 'edit' : 'created', 'success', data ? 'Zaktualizowano przedział płatności' : 'Dodano nowy przedział płatności', '');
      handleClose();
    } else {
      setSeverity('error');
      setTitle('Nie udało się dodać nowego przedział płatnościu');
      setMessage('');
      setShowAlert(true);
    }
  };

  const handleDelete = async () => {
    const { error, payload } = await mutate([], 'DELETE', data.id);
    if (!error) {
      submitCallback(data, 'delete', 'success', `Usunięto przedział płatności ${data.name}`, '');
      handleClose();
    } else {
      setSeverity('error');
      setTitle(`Nie udało się usunąć przedział płatnościu ${data.name}`);
      setMessage(payload);
      setShowAlert(true);
    }
  };

  const addEmptyProduct = () => {
    setPositions([...positions, { id: Date.now(), participantsFrom: '0', participantsTo: '0', grossValue: '0' }]);
  };

  const removePositionCallback = id => {
    setPositions(_.filter(positions, obj => obj.id !== id));
  };

  const updatePositionCallback = position => {
    const clonePositions = _.cloneDeep(positions);
    const index = clonePositions.findIndex(obj => obj.id == position.id);
    clonePositions.splice(index, 1);
    clonePositions.splice(index, 0, position);
    setPositions(clonePositions);
  };

  const getAllErrors = () => {
    const errorsArr = [];
    _.map(positions, (position, key) => {
      errorsArr.push(dynamicRef.current[key].getErrors());
    });

    return errorsArr;
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: values => {
      handleSubmit(values);
    }
  });

  return (
    <>
      {showAlert && (
        <AlertDialogContainer
          severity={severity}
          title={title}
          message={message}
          setShowAlert={setShowAlert}
        />
      )}
      <form onSubmit={formik.handleSubmit} style={{ textAlign: 'center' }}>
        <Box display="flex" flexDirection="column" mt={1} p={1} ml="5%" mr="5%">
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 25%">Data od</Typography>
            <SmallFormDateField
              sx={{ flex: '1 1 90%' }}
              name="date_from"
              id="date_from"
              type="text"
              placeholder="date_from"
              value={formik.values.date_from}
              onChange={val => formik.setFieldValue('date_from', formatDate(val))}
              error={formik.touched.date_from && Boolean(formik.errors.date_from)}
              helperText={formik.touched.date_from && formik.errors.date_from}
            />
          </Stack>
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 25%">Data do</Typography>
            <SmallFormDateField
              sx={{ flex: '1 1 90%' }}
              name="date_to"
              id="date_to"
              type="text"
              placeholder="date_to"
              value={formik.values.date_to}
              onChange={val => formik.setFieldValue('date_to', formatDate(val))}
              error={formik.touched.date_to && Boolean(formik.errors.date_to)}
              helperText={formik.touched.date_to && formik.errors.date_to}
            />
          </Stack>
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 25%">Wartość za płatnego opiekuna (zł)</Typography>
            <DebounceInput
              sx={{ flex: '1 1 90%' }}
              name="gross_value_per_paid_guardian"
              id="gross_value_per_paid_guardian"
              type="text"
              placeholder="wartość"
              value={formik.values.gross_value_per_paid_guardian}
              onChange={formik.handleChange}
              error={formik.touched.gross_value_per_paid_guardian && Boolean(formik.errors.gross_value_per_paid_guardian)}
              helperText={formik.touched.gross_value_per_paid_guardian && formik.errors.gross_value_per_paid_guardian}
              debounceTimeout={300}
              element={SmallFormTextField}
            />
          </Stack>
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 25%">Cel minimalny Giewartów</Typography>
            <DebounceInput
              sx={{ flex: '1 1 90%' }}
              name="minimum_goal_giewartow"
              id="minimum_goal_giewartow"
              type="text"
              placeholder="wartość"
              value={formik.values.minimum_goal_giewartow}
              onChange={formik.handleChange}
              error={formik.touched.minimum_goal_giewartow && Boolean(formik.errors.minimum_goal_giewartow)}
              helperText={formik.touched.minimum_goal_giewartow && formik.errors.minimum_goal_giewartow}
              debounceTimeout={300}
              element={SmallFormTextField}
            />
          </Stack>
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 25%">Cel maksymalny Giewartów</Typography>
            <DebounceInput
              sx={{ flex: '1 1 90%' }}
              name="maximum_goal_giewartow"
              id="maximum_goal_giewartow"
              type="text"
              placeholder="wartość"
              value={formik.values.maximum_goal_giewartow}
              onChange={formik.handleChange}
              error={formik.touched.maximum_goal_giewartow && Boolean(formik.errors.maximum_goal_giewartow)}
              helperText={formik.touched.maximum_goal_giewartow && formik.errors.maximum_goal_giewartow}
              debounceTimeout={300}
              element={SmallFormTextField}
            />
          </Stack>
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 25%">Cel minimalny Mrówki</Typography>
            <DebounceInput
              sx={{ flex: '1 1 90%' }}
              name="minimum_goal_mrowki"
              id="minimum_goal_mrowki"
              type="text"
              placeholder="wartość"
              value={formik.values.minimum_goal_mrowki}
              onChange={formik.handleChange}
              error={formik.touched.minimum_goal_mrowki && Boolean(formik.errors.minimum_goal_mrowki)}
              helperText={formik.touched.minimum_goal_mrowki && formik.errors.minimum_goal_mrowki}
              debounceTimeout={300}
              element={SmallFormTextField}
            />
          </Stack>
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 25%">Cel maksymalny Mrówki</Typography>
            <DebounceInput
              sx={{ flex: '1 1 90%' }}
              name="maximum_goal_mrowki"
              id="maximum_goal_mrowki"
              type="text"
              placeholder="wartość"
              value={formik.values.maximum_goal_mrowki}
              onChange={formik.handleChange}
              error={formik.touched.maximum_goal_mrowki && Boolean(formik.errors.maximum_goal_mrowki)}
              helperText={formik.touched.maximum_goal_mrowki && formik.errors.maximum_goal_mrowki}
              debounceTimeout={300}
              element={SmallFormTextField}
            />
          </Stack>
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 10%">Terminy</Typography>
            <SmallFormTextField
              sx={{ flex: '1 1 90%' }}
              select
              multiple
              id="periods"
              name="periods"
              options={allChoices.periods}
              value={formik.values.periods}
              onChange={formik.handleChange} />
          </Stack>
          {formik.values.periods.length > 0 && <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 10%">Pakiety</Typography>
            <SmallFormTextField
              sx={{ flex: '1 1 90%' }}
              select
              multiple
              id="packages"
              name="packages"
              options={allChoices.packages.filter(option => formik.values.periods.includes(option.period))}
              value={formik.values.packages}
              onChange={formik.handleChange} />
          </Stack>}
          <Button variant="contained" startIcon={<AddIcon />} sx={{ mb: 1, mt: 1, mr: 1 }} onClick={() => addEmptyProduct()}>
        Dodaj przedział płatności dla uczestników
          </Button>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell align="center">Ilość Uczestników Od</TableCell>
                  <TableCell align="center">Ilość Uczestników Do</TableCell>
                  <TableCell align="center">Wartość (zł)</TableCell>
                  <TableCell>&nbsp;</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {positions.map((product, key) => (
                  <Row
                    ref={el => (dynamicRef.current[key] = el)}
                    row={product}
                    removeProductCallback={removePositionCallback}
                    key={key}
                    index={key}
                    updateProductCallback={updatePositionCallback} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
        <Button
          color="primary"
          variant="contained"
          type="submit"
          sx={{ mt: 3 }}
          disabled={loading}
        >
          <AddIcon />
          Zapisz
        </Button>
        { data
          ? <Button
            color="error"
            variant="contained"
            sx={{ mt: 3, ml: 4 }}
            onClick={() => {
              setConfirmDeleteOpen(true);
            }}
          >
            <DeleteIcon />
          Usuń
          </Button>
          : null}
      </form>
      <ConfirmDialog
        title="Usunąć przedział płatności?"
        open={confirmDeleteOpen}
        setOpen={setConfirmDeleteOpen}
        onConfirm={handleDelete}
        disabled={loading}
      >
        Czy na pewno chcesz usunąć ten przedział płatności?
      </ConfirmDialog>
    </>

  );
};
